import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

export interface PlatformFeatures {
  summerBridgeEnabled: boolean;
  studioEnabled: boolean;
  newSignupsEnabled: boolean;
  maintenanceMode: boolean;
}

export interface PlatformAnnouncement {
  enabled: boolean;
  message: string;
  type: 'info' | 'warning' | 'success';
}

interface ConfigContextValue {
  features: PlatformFeatures;
  announcement: PlatformAnnouncement;
  isLoaded: boolean;
}

const DEFAULTS: ConfigContextValue = {
  features: {
    summerBridgeEnabled: true,
    studioEnabled: true,
    newSignupsEnabled: true,
    maintenanceMode: false,
  },
  announcement: { enabled: false, message: '', type: 'info' },
  isLoaded: false,
};

const ConfigContext = createContext<ConfigContextValue>(DEFAULTS);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<ConfigContextValue>(DEFAULTS);

  useEffect(() => {
    fetch(`${API_BASE}/config`)
      .then(r => r.json())
      .then(data => setConfig({ features: data.features, announcement: data.announcement, isLoaded: true }))
      .catch(() => setConfig(c => ({ ...c, isLoaded: true }))); // fall through to defaults on error
  }, []);

  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig() {
  return useContext(ConfigContext);
}
